import { formatPesoAmount } from '../utils/money';
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getPurchasesByDate, deletePendingPurchase } from '../api';
import { FiChevronDown, FiTrash2, FiFilter, FiCopy, FiUsers } from 'react-icons/fi';
import PurchaseFilters from '../components/PurchaseFilters';
import useCurrentUser from '../hooks/useCurrentUser';
import usePurchaseFilters from '../hooks/usePurchaseFilters';
import { hasPermission } from '../utils/permissions';
import CancelPendingSaleModal from '../components/CancelPendingSaleModal';
import { getSaleStatusMeta } from '../utils/saleStatus';
import { normalizePaginatedResponse } from '../utils/pagination';
import InlineLoading from '../components/InlineLoading';
import { notify } from '../utils/alertBus';
import {
  getPaymentTerm,
  getCreditDueDate,
  isPurchaseOverdue,
} from '../utils/purchaseUtils';
import { CANCEL_REASON_CATEGORY_MAP } from '../constants/cancelReasonCategories';

const PurchasesListPage = () => {
  const [purchases, setPurchases] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPurchases, setTotalPurchases] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [documentType, setDocumentType] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('');
  const [expandedPurchases, setExpandedPurchases] = useState(() => new Set());
  const { user } = useCurrentUser();
  const { filters, setters } = usePurchaseFilters({ includeSearch: true });
  const { startDate, endDate, searchTerm } = filters;
  const navigate = useNavigate();
  const branchId = useParams().branchId || localStorage.getItem('selectedBranchId');
  const pageSize = 12;
  const toneStyles = {
    gray: { badge: 'border-slate-200 bg-slate-100 text-slate-600' },
    amber: { badge: 'border-amber-200 bg-amber-50 text-amber-700' },
    indigo: { badge: 'border-indigo-200 bg-indigo-50 text-indigo-700' },
    emerald: { badge: 'border-emerald-200 bg-emerald-50 text-emerald-700' },
    sky: { badge: 'border-sky-200 bg-sky-50 text-sky-700' },
    red: { badge: 'border-red-200 bg-red-50 text-red-700' },
  };
  const resolveDocumentTone = (value) => {
    if (value === 'boleta') return 'amber';
    if (value === 'factura') return 'indigo';
    return 'indigo';
  };
  const resolvePaymentTone = (purchase) => {
    const statusNormalized = String(purchase?.saleStatus || '').toLowerCase();
    if (statusNormalized === 'cancelled' || purchase?.isDeleted) return 'red';
    const term = getPaymentTerm(purchase);
    if (term === 'credit') return 'sky';
    const status = purchase?.paymentStatus || (purchase?.isPaid ? 'paid' : 'unpaid');
    if (status === 'paid') return 'emerald';
    if (status === 'partial') return 'amber';
    return 'gray';
  };
  const resolvePaymentLabel = (purchase) => {
    const statusNormalized = String(purchase?.saleStatus || '').toLowerCase();
    if (statusNormalized === 'cancelled' || purchase?.isDeleted) return 'Eliminada';
    const term = getPaymentTerm(purchase);
    if (term === 'credit') return 'Crédito';
    const status = purchase?.paymentStatus || (purchase?.isPaid ? 'paid' : 'unpaid');
    if (status === 'paid') return 'Pagada';
    if (status === 'partial') return 'Parcial';
    return 'Pendiente';
  };
  const docLabel = (purchase) => {
    const type = (purchase?.documentType || '').toLowerCase();
    if (type === 'boleta') return 'Boleta';
    if (type === 'factura') return 'Factura';
    return 'Factura';
  };
  const formatMoney = (value) => formatPesoAmount(Number(value || 0));
  const formatDateTime = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString('es-CL');
  };
  const getCancelReasonText = (purchase) => {
    if (!purchase) return null;
    const category = String(purchase.cancelReasonCategory || '').trim().toLowerCase();
    const detail = String(purchase.cancelReasonDetail || '').trim();
    if (category) {
      const categoryLabel = CANCEL_REASON_CATEGORY_MAP[category]?.label || category;
      return detail ? `${categoryLabel}: ${detail}` : categoryLabel;
    }
    const legacyReason = String(purchase.cancelReason || '').trim();
    return legacyReason || null;
  };
  const togglePurchaseDetails = (purchaseId) => {
    setExpandedPurchases((prev) => {
      const next = new Set(prev);
      if (next.has(purchaseId)) {
        next.delete(purchaseId);
      } else {
        next.add(purchaseId);
      }
      return next;
    });
  };
  const copyToClipboard = async (text) => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'absolute';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      notify('Copiado al portapapeles.');
    } catch (error) {
      console.error('Error copying purchase', error);
      notify('No se pudo copiar.', { type: 'error' });
    }
  };
  const buildPurchaseCopy = (purchase) => {
    const clientName = purchase?.Client?.name || 'Venta Detalle';
    const clientRut = purchase?.Client?.rut ? `RUT: ${purchase.Client.rut}` : null;
    const status = purchase.paymentStatus || (purchase.isPaid ? 'paid' : 'unpaid');
    const statusLabel = {
      unpaid: 'Pendiente',
      partial: 'Parcial',
      paid: 'Pagada',
    };
    const term = getPaymentTerm(purchase);
    const dueDate = getCreditDueDate(purchase);
    const overdue = isPurchaseOverdue(purchase);
    const statusMeta = getSaleStatusMeta(purchase.saleStatus);
    const statusNormalized = String(purchase.saleStatus || '').toLowerCase();
    const isCancelled = statusNormalized === 'cancelled' || purchase.isDeleted;
    const paymentLabel = resolvePaymentLabel(purchase);
    const cancelReasonText = getCancelReasonText(purchase);
    const saleStatusLabel = isCancelled
      ? 'Eliminada'
      : (purchase.saleStatus === 'suspended'
        ? statusMeta.label
        : statusLabel[status] || 'Pendiente');

    return [
      `${docLabel(purchase)} #${purchase.id}`,
      `Cliente: ${clientName}`,
      clientRut,
      `Total: $${formatMoney(purchase.totalAmount)}`,
      `Pago: ${paymentLabel}`,
      `Estado: ${saleStatusLabel}`,
      `Condicion: ${term === 'credit' ? 'Crédito' : 'Contado'}`,
      purchase.balance !== undefined
        ? `Pendiente: $${formatMoney(purchase.balance || 0)}`
        : null,
      term === 'credit' && dueDate
        ? `Vence: ${dueDate.toLocaleDateString('es-CL')}${overdue ? ' (Vencido)' : ''}`
        : null,
      isCancelled ? `Motivo anulacion: ${cancelReasonText || 'Sin motivo registrado'}` : null,
      purchase.createdAt ? `Fecha: ${formatDateTime(purchase.createdAt)}` : null,
    ]
      .filter(Boolean)
      .join('\n');
  };
  useEffect(() => {
    let active = true;
    const fetchPurchases = async () => {
      if (!branchId) return;
      setLoading(true);
      setError('');
      try {
        const trimmedSearch = searchTerm.trim();
        const params = {
          page,
          pageSize,
          includeDeleted: true,
          ...(trimmedSearch ? { search: trimmedSearch } : {}),
        };
        const data = await getPurchasesByDate(branchId, startDate, endDate, {
          ...params,
          ...(documentType ? { documentType } : {}),
          ...(paymentFilter ? { paymentFilter } : {}),
        });
        if (!active) return;
        const normalized = normalizePaginatedResponse(data);
        setPurchases(normalized.data || []);
        const normalizedTotal = normalized.total || 0;
        setTotalPurchases(normalizedTotal);
        setTotalPages(
          normalized.totalPages || Math.max(1, Math.ceil(normalizedTotal / pageSize))
        );
      } catch (err) {
        if (!active) return;
        setError(err.message);
        setPurchases([]);
        setTotalPurchases(0);
        setTotalPages(1);
      } finally {
        if (active) setLoading(false);
      }
    };

    // Combine keystrokes into one search; late responses from previous filters
    // must never replace the currently selected page or store.
    const timer = setTimeout(fetchPurchases, searchTerm.trim() ? 250 : 0);
    return () => { active = false; clearTimeout(timer); };
  }, [startDate, endDate, searchTerm, page, branchId, documentType, paymentFilter]);

  useEffect(() => {
    setPage(1);
  }, [startDate, endDate, searchTerm, branchId, documentType, paymentFilter]);

  const handleDelete = (purchase) => {
    if (!hasPermission(user, 'sales.delete')) return;
    if (!purchase?.id) return;
    setCancelTarget(purchase);
  };

  const handleConfirmCancel = async (cancelPayload) => {
    if (!hasPermission(user, 'sales.delete')) return;
    if (!branchId || !cancelTarget?.id) {
      throw new Error('No se pudo identificar la venta.');
    }
    try {
      await deletePendingPurchase(branchId, cancelTarget.id, cancelPayload || {});
      const nextCategory = String(cancelPayload?.cancelReasonCategory || '').trim().toLowerCase() || null;
      const nextDetail = String(cancelPayload?.cancelReasonDetail || '').trim() || null;
      const nextLegacyReason = String(cancelPayload?.cancelReason || '').trim() || null;
      setPurchases((prev) =>
        prev.map((purchase) =>
          purchase.id === cancelTarget.id
            ? {
              ...purchase,
              isDeleted: true,
              saleStatus: 'cancelled',
              cancelReasonCategory: nextCategory,
              cancelReasonDetail: nextDetail,
              cancelReason: nextLegacyReason,
            }
            : purchase
        )
      );
    } catch (error) {
      console.error('Error deleting purchase:', error.message);
      throw new Error(
        error?.response?.data?.message || 'No se pudo anular la venta pendiente.'
      );
    }
  };

  const visiblePurchases = purchases.filter(Boolean);
  const currentPage = Math.min(page, totalPages);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  return (
    <main className="record-list-page">
      <div className="mx-auto max-w-screen-2xl space-y-4">
        <section className="record-list-panel" aria-label="Gestión de ventas">
          <div className="record-list-header">
            <div className="flex items-center gap-3">
              <h1 className="record-list-title">Ventas</h1>
              <span className="border-l border-slate-200 pl-3 text-xs font-medium text-slate-500">{totalPurchases} ventas</span>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setShowFilters(!showFilters)}
                aria-label={showFilters ? 'Ocultar filtros' : 'Mostrar filtros'}
                aria-expanded={showFilters}
                aria-controls="sales-list-filters"
                className="record-list-header-action sm:hidden"
              >
                <FiFilter className="h-4 w-4" aria-hidden="true" />
                Filtros
              </button>
              {hasPermission(user, 'client.read') && (
                <Link to="/client" className="record-list-header-action">
                  <FiUsers className="h-4 w-4" aria-hidden="true" />
                  Clientes
                </Link>
              )}
            </div>
          </div>
          {showFilters && (
            <div id="sales-list-filters" className="record-list-filters">
              <PurchaseFilters
                filters={filters}
                setters={setters}
                showSearch
                showDocumentType
                documentType={documentType}
                onDocumentTypeChange={setDocumentType}
                showPaymentStatus
                paymentFilter={paymentFilter}
                onPaymentFilterChange={setPaymentFilter}
                showPaymentTerm={false}
                showOverdue={false}
                showDueRange={false}
                layout="row"
                containerClassName=""
              />
            </div>
          )}
        </section>

        {error && <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">{error}</p>}

        <div className="record-list-scroll" role="region" aria-label="Tabla de ventas" tabIndex={0}>
          <table className="record-list-table min-w-[1100px]" aria-label="Lista de ventas" aria-busy={loading}>
            <thead>
              <tr>
                <th scope="col" className="font-bold">Venta</th>
                <th scope="col" className="font-bold">Cliente</th>
                <th scope="col" className="font-bold">Fecha</th>
                <th scope="col" className="text-right font-bold">Total</th>
                <th scope="col" className="font-bold">Pago</th>
                <th scope="col" className="font-bold">Estado</th>
                <th scope="col" className="text-right font-bold">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                    <InlineLoading label="Cargando ventas" />
                  </td>
                </tr>
              ) : visiblePurchases.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                    No hay ventas en este rango de fechas.
                  </td>
                </tr>
              ) : (
                visiblePurchases.map((purchase) => {
                  const documentTypeValue = (purchase?.documentType || '').toLowerCase();
                  const docTone = resolveDocumentTone(documentTypeValue);
                  const docStyles = toneStyles[docTone] || toneStyles.gray;
                  const paymentTone = resolvePaymentTone(purchase);
                  const paymentStyles = toneStyles[paymentTone] || toneStyles.gray;
                  const paymentLabel = resolvePaymentLabel(purchase);
                  const status = purchase.paymentStatus || (purchase.isPaid ? 'paid' : 'unpaid');
                  const statusMeta = getSaleStatusMeta(purchase.saleStatus);
                  const statusLabel = {
                    unpaid: 'Pendiente',
                    partial: 'Parcial',
                    paid: 'Pagada',
                  };
                  const term = getPaymentTerm(purchase);
                  const dueDate = getCreditDueDate(purchase);
                  const overdue = isPurchaseOverdue(purchase);
                  const statusNormalized = (purchase.saleStatus || '').toLowerCase();
                  const isOpen = statusNormalized === 'open';
                  const isSuspended = statusNormalized === 'suspended';
                  const isCompleted = statusNormalized === 'completed' || status === 'paid' || purchase.isPaid;
                  const isCancelled = statusNormalized === 'cancelled' || purchase.isDeleted;
                  const cancelReasonText = getCancelReasonText(purchase);
                  const needsPayment =
                    statusNormalized === 'completed'
                    && !purchase.isDeleted
                    && purchase.paymentTerm !== 'credit'
                    && !purchase.paymentMethod
                    && !purchase.isPaid;
                  const clientName = purchase?.Client?.name || 'Venta Detalle';
                  const clientRut = purchase?.Client?.rut;
                  const isExpanded = expandedPurchases.has(purchase.id);
                  const detailsId = `purchase-${purchase.id}-details`;
                  const saleStatusLabel = isCancelled
                    ? 'Eliminada'
                    : (purchase.saleStatus === 'suspended'
                      ? statusMeta.label
                      : statusLabel[status] || 'Pendiente');
                  const saleStatusDetailLabel = isCancelled ? 'Eliminada' : statusMeta.label;
                  const canDelete = status === 'unpaid' && isOpen;
                  return (
                    <React.Fragment key={purchase.id}>
                      <tr className="record-list-row" data-expanded={isExpanded} data-cancelled={isCancelled}>
                        <th scope="row" className="font-bold text-slate-950">
                          <button
                            type="button"
                            onClick={() => togglePurchaseDetails(purchase.id)}
                            className="flex w-24 items-center gap-2 rounded-md text-left outline-none hover:text-blue-700 focus-visible:ring-2 focus-visible:ring-blue-500"
                            aria-label={`${isExpanded ? 'Ocultar' : 'Ver'} más de venta #${purchase.id}`}
                            aria-expanded={isExpanded}
                            aria-controls={detailsId}
                          >
                            <FiChevronDown className={`shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} aria-hidden="true" />
                            <span className="flex flex-col items-start gap-1">
                              <span>#{purchase.id}</span>
                              <span className={`record-list-badge ${docStyles.badge}`}>
                                {docLabel(purchase)}
                              </span>
                            </span>
                          </button>
                        </th>
                        <td>
                          <div className="min-w-[160px] max-w-xs break-words font-medium text-slate-900">{clientName}</div>
                          <div className="mt-1 whitespace-nowrap text-xs text-slate-500">{clientRut || '—'}</div>
                        </td>
                        <td className="text-xs text-slate-600">{formatDateTime(purchase.createdAt)}</td>
                        <td className="whitespace-nowrap text-right font-bold tabular-nums text-slate-900">
                          ${formatMoney(purchase.totalAmount)}
                        </td>
                        <td>
                          <span className={`record-list-badge min-w-[5.5rem] whitespace-nowrap ${paymentStyles.badge}`}>
                            {paymentLabel}
                          </span>
                        </td>
                        <td className="text-slate-600">
                          <div>{saleStatusLabel}</div>
                          {isCancelled && (
                            <p className="mt-1 min-w-[160px] max-w-xs break-words text-xs text-red-700">
                              Motivo anulacion: {cancelReasonText || 'Sin motivo registrado'}
                            </p>
                          )}
                        </td>
                        <td>
                          <div className="ml-auto flex w-64 flex-wrap justify-end gap-2 text-xs">
                            {canDelete && hasPermission(user, 'sales.delete') && !isCancelled && (
                              <button
                                type="button"
                                title="Anular venta"
                                aria-label={`Anular venta #${purchase.id}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(purchase);
                                }}
                                className="record-list-action record-list-action-danger"
                              >
                                <FiTrash2 size={16} />
                              </button>
                            )}
                            <button
                              onClick={() => copyToClipboard(buildPurchaseCopy(purchase))}
                              className="record-list-action"
                              type="button"
                            >
                              <FiCopy size={14} />
                              Copiar
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/branches/${branchId}/purchases/${purchase.id}`);
                              }}
                              className="record-list-action record-list-action-primary"
                            >
                              Ver
                            </button>
                            {(isOpen || isSuspended) && !isCancelled && hasPermission(user, 'sales.update') && (
                              <button
                                onClick={() => navigate(`/branches/${branchId}/purchases/${purchase.id}/edit`)}
                                className="record-list-action record-list-action-primary"
                              >
                                Continuar compra
                              </button>
                            )}
                            {needsPayment && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/branches/${branchId}/purchases/${purchase.id}/receipt`);
                                }}
                                className="record-list-action record-list-action-success"
                              >
                                Registrar pago
                              </button>
                            )}
                            {isCompleted && !needsPayment && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/branches/${branchId}/purchases/${purchase.id}/receipt`);
                                }}
                                className="record-list-action record-list-action-success"
                              >
                                Comprobante
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr id={detailsId} className="record-list-details" data-cancelled={isCancelled}>
                          <td colSpan={7} className="px-4 py-4">
                            <div className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm text-slate-600 sm:grid-cols-2 lg:grid-cols-3">
                              <p>RUT: {clientRut || '—'}</p>
                              <p>Condición: {term === 'credit' ? 'Crédito' : 'Contado'}</p>
                              {purchase.balance !== undefined && (
                                <p>Pendiente: ${formatMoney(purchase.balance || 0)}</p>
                              )}
                              {term === 'credit' && dueDate && (
                                <p className={overdue ? 'text-red-600' : 'text-slate-600'}>
                                  Vence: {dueDate.toLocaleDateString('es-CL')}
                                  {overdue ? ' (Vencido)' : ''}
                                </p>
                              )}
                              <p>Estado venta: {saleStatusDetailLabel}</p>
                              <p>Estado pago: {paymentLabel}</p>
                              {isCancelled && (
                                <p className="text-red-700">
                                  Motivo anulacion: {cancelReasonText || 'Sin motivo registrado'}
                                </p>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {totalPurchases > pageSize && (
          <div className="record-list-pagination">
            <div>
              Mostrando {totalPurchases === 0 ? 0 : (currentPage - 1) * pageSize + 1}-
              {Math.min(currentPage * pageSize, totalPurchases)} de {totalPurchases}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="record-list-action"
              >
                Anterior
              </button>
              <span>
                {currentPage} / {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="record-list-action"
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>
      <CancelPendingSaleModal
        isOpen={!!cancelTarget}
        onClose={() => setCancelTarget(null)}
        saleLabel={cancelTarget ? `${docLabel(cancelTarget)} #${cancelTarget.id}` : ''}
        onConfirm={handleConfirmCancel}
      />
    </main>
  );
};

export default PurchasesListPage;
